import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  updateDoc,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  Timestamp,
  writeBatch,
  arrayRemove,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { User as FirebaseUser } from 'firebase/auth';
import { UserProfile, UserFilters, UserSort, UserRole, DataDeletionRequest } from '../types/user';

// Collections
const usersCollection = collection(db, 'users');
const postsCollection = collection(db, 'posts');
const commentsCollection = collection(db, 'comments');
const reactionsCollection = collection(db, 'reactions');
const pollsCollection = collection(db, 'polls');

export const createOrUpdateUser = async (user: FirebaseUser) => {
  try {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      // Create new user profile
      const newUserProfile: UserProfile = {
        id: user.uid,
        name: user.displayName || 'Anonymous',
        email: user.email || '',
        avatar: user.photoURL || '',
        role: 'member',
        status: 'active',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        lastActive: Timestamp.now(),
        stats: {
          eventsCreated: 0,
          eventsAttended: 0,
          commentsPosted: 0,
          reportsSubmitted: 0,
          eventsRSVPd: 0,
          pollsCreated: 0,
          pollVotes: 0,
        },
        preferences: {
          notifications: true,
          theme: 'dark'
        }
      };

      await setDoc(userRef, newUserProfile);
      return newUserProfile;
    } else {
      // Update existing user
      const updateData = {
        name: user.displayName || 'Anonymous',
        email: user.email || '',
        avatar: user.photoURL || '',
        updatedAt: Timestamp.now(),
        lastActive: Timestamp.now()
      };

      await updateDoc(userRef, updateData);
      return { id: user.uid, ...userDoc.data(), ...updateData } as UserProfile;
    }
  } catch (error) {
    console.error('Error creating/updating user:', error);
    throw error;
  }
};

export const getUsers = async (
  filters: UserFilters,
  sort: UserSort,
  lastDoc?: DocumentSnapshot
): Promise<{ users: UserProfile[]; lastDoc: DocumentSnapshot | null }> => {
  try {
    let q = query(usersCollection);

    // Apply filters
    if (filters.role) {
      q = query(q, where('role', '==', filters.role));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    // Apply sorting
    q = query(q, orderBy(sort.field, sort.direction));

    // Apply pagination
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    q = query(q, limit(10));

    const querySnapshot = await getDocs(q);
    const users = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as UserProfile[];

    // Apply search filter after fetching (since Firestore doesn't support text search)
    let filteredUsers = users;
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase();
      filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      );
    }

    const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    return { users: filteredUsers, lastDoc: newLastDoc };
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const updateUserRole = async (userId: string, role: UserRole): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

export const updateUserStatus = async (
  userId: string,
  status: 'active' | 'suspended' | 'banned',
  reason?: string,
  suspendedUntil?: Timestamp
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    const updateData: any = {
      status,
      updatedAt: Timestamp.now()
    };

    if (status === 'banned' && reason) {
      updateData.banReason = reason;
    } else if (status === 'suspended') {
      if (reason) updateData.suspensionReason = reason;
      if (suspendedUntil) updateData.suspendedUntil = suspendedUntil;
    } else {
      // Clear suspension/ban fields when status is active
      updateData.suspensionReason = null;
      updateData.suspendedUntil = null;
      updateData.banReason = null;
    }

    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

export const updateUserNotes = async (userId: string, notes: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      notes,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating user notes:', error);
    throw error;
  }
};

export const checkSuspensionStatus = async (userId: string): Promise<boolean> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data() as UserProfile;

    if (userData.status === 'banned') {
      return false;
    }

    if (userData.status === 'suspended' && userData.suspendedUntil) {
      const now = Timestamp.now();
      if (now.toDate() < userData.suspendedUntil.toDate()) {
        return false;
      } else {
        // Suspension has expired, update status to active
        await updateDoc(userRef, {
          status: 'active',
          suspensionReason: null,
          suspendedUntil: null,
          updatedAt: Timestamp.now()
        });
      }
    }

    return true;
  } catch (error) {
    console.error('Error checking suspension status:', error);
    return false;
  }
};

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return null;
    }

    return { id: userDoc.id, ...userDoc.data() } as UserProfile;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const deleteUser = async (userId: string): Promise<void> => {
  try {
    const batch = writeBatch(db);

    // Get all user's content
    const [userComments, userReactions, userPolls, userPosts] = await Promise.all([
      getDocs(query(commentsCollection, where('author.id', '==', userId))),
      getDocs(query(reactionsCollection, where('userId', '==', userId))),
      getDocs(query(pollsCollection, where('createdBy.id', '==', userId))),
      getDocs(query(postsCollection, where('author.id', '==', userId)))
    ]);

    // Delete user's content
    for (const comment of userComments.docs) {
      batch.delete(comment.ref);
    }

    for (const reaction of userReactions.docs) {
      batch.delete(reaction.ref);
    }

    for (const poll of userPolls.docs) {
      batch.delete(poll.ref);
    }

    for (const post of userPosts.docs) {
      batch.delete(post.ref);
    }

    // Delete the user document
    const userRef = doc(db, 'users', userId);
    batch.delete(userRef);

    await batch.commit();
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const createDataDeletionRequest = async (userId: string, reason: string) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data() as UserProfile;

    const requestRef = doc(collection(db, 'dataDeletionRequests'));
    await setDoc(requestRef, {
      userId,
      userEmail: userData.email,
      userName: userData.name,
      reason,
      status: 'pending',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return requestRef.id;
  } catch (error) {
    console.error('Error creating data deletion request:', error);
    throw error;
  }
};

export const getDataDeletionRequests = async (
  status?: 'pending' | 'completed' | 'rejected',
  pageSize: number = 10,
  lastDoc?: DocumentSnapshot
): Promise<{ requests: DataDeletionRequest[]; lastDoc: DocumentSnapshot | null }> => {
  try {
    let requestsQuery = query(
      collection(db, 'dataDeletionRequests'),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    // Add status filter if provided
    if (status) {
      requestsQuery = query(
        collection(db, 'dataDeletionRequests'),
        where('status', '==', status),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    }

    // Add pagination if lastDoc is provided
    if (lastDoc) {
      requestsQuery = query(
        collection(db, 'dataDeletionRequests'),
        ...(status ? [where('status', '==', status)] : []),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(pageSize)
      );
    }

    const querySnapshot = await getDocs(requestsQuery);
    const requests = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as DataDeletionRequest[];

    return {
      requests,
      lastDoc: querySnapshot.docs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : null
    };
  } catch (error) {
    console.error('Error fetching data deletion requests:', error);
    throw error;
  }
};

export const backupUserData = async (userId: string) => {
  try {
    const backupId = `backup_${userId}_${Date.now()}`;
    const backupRef = doc(collection(db, 'userBackups'), backupId);

    // Get user's content from all collections
    const [
      userComments, 
      userReactions, 
      userPolls, 
      userPosts,
      userVideos,
      userEvents,
      userRSVPs,
      userFriendRequests,
      userNotifications,
      userReports,
      userVotes,
      userEventComments,
      userPollVotes,
      userModeratorNotes,
      userArticles
    ] = await Promise.all([
      getDocs(query(commentsCollection, where('author.id', '==', userId))),
      getDocs(query(reactionsCollection, where('userId', '==', userId))),
      getDocs(query(pollsCollection, where('createdBy.id', '==', userId))),
      getDocs(query(collection(db, 'posts'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'videos'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'events'), where('organizer.uid', '==', userId))),
      getDocs(query(collection(db, 'rsvps'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'friendRequests'), where('senderId', '==', userId))),
      getDocs(query(collection(db, 'friendRequests'), where('receiverId', '==', userId))),
      getDocs(query(collection(db, 'notifications'), where('recipientId', '==', userId))),
      getDocs(query(collection(db, 'reports'), where('reportedBy.id', '==', userId))),
      getDocs(query(collection(db, 'votes'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'comments'), where('author.id', '==', userId))),
      getDocs(query(collection(db, 'poll_votes'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'moderator_notes'), where('createdBy.id', '==', userId))),
      getDocs(query(collection(db, 'articles'), where('userId', '==', userId)))
    ]);

    // Get video comments and reactions for backup
    const videoComments: any[] = [];
    const videoReactions: any[] = [];
    
    for (const videoDoc of userVideos.docs) {
      const comments = await getDocs(collection(db, 'videos', videoDoc.id, 'comments'));
      const reactions = await getDocs(collection(db, 'videos', videoDoc.id, 'reactions'));
      
      videoComments.push(...comments.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      videoReactions.push(...reactions.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }

    const backupData = {
      userId,
      createdAt: Timestamp.now(),
      comments: userComments.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      reactions: userReactions.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      polls: userPolls.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      posts: userPosts.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      videos: userVideos.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      events: userEvents.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      rsvps: userRSVPs.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      friendRequests: userFriendRequests.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      notifications: userNotifications.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      reports: userReports.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      votes: userVotes.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      eventComments: userEventComments.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      pollVotes: userPollVotes.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      moderatorNotes: userModeratorNotes.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      articles: userArticles.docs.map(doc => ({ id: doc.id, ...doc.data() })),
      videoComments,
      videoReactions
    };

    // Get user document for friends list
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.exists() ? userDoc.data() : null;
    
    // Add friends list to backup data
    (backupData as any).friends = userData?.friends || [];

    await setDoc(backupRef, backupData);
    return backupId;
  } catch (error) {
    console.error('Error backing up user data:', error);
    throw error;
  }
};

export const deleteUserData = async (userId: string, requestId: string) => {
  try {
    const batch = writeBatch(db);

    // Get all user's content from various collections
    const [
      userComments, 
      userReactions, 
      userPolls, 
      userPosts,
      userVideos,
      userEvents,
      userRSVPs,
      userFriendRequests,
      userNotifications,
      userReports,
      userVotes,
      userEventComments,
      userPollVotes,
      userModeratorNotes,
      userArticles,
      userPostComments,
      userArticleComments,
      userVideoComments,
      userPostReactions,
      userArticleReactions,
      userVideoReactions
    ] = await Promise.all([
      getDocs(query(commentsCollection, where('author.id', '==', userId))),
      getDocs(query(reactionsCollection, where('userId', '==', userId))),
      getDocs(query(pollsCollection, where('createdBy.id', '==', userId))),
      getDocs(query(collection(db, 'posts'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'videos'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'events'), where('organizer.uid', '==', userId))),
      getDocs(query(collection(db, 'rsvps'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'friendRequests'), where('senderId', '==', userId))),
      getDocs(query(collection(db, 'friendRequests'), where('receiverId', '==', userId))),
      getDocs(query(collection(db, 'notifications'), where('recipientId', '==', userId))),
      getDocs(query(collection(db, 'reports'), where('reportedBy.id', '==', userId))),
      getDocs(query(collection(db, 'votes'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'comments'), where('author.id', '==', userId))),
      getDocs(query(collection(db, 'poll_votes'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'moderator_notes'), where('createdBy.id', '==', userId))),
      getDocs(query(collection(db, 'articles'), where('userId', '==', userId))),
      // Get comments from posts subcollections
      getDocs(query(collection(db, 'posts'), where('userId', '==', userId))).then(async (posts) => {
        const allComments = [];
        for (const post of posts.docs) {
          const comments = await getDocs(collection(db, 'posts', post.id, 'comments'));
          allComments.push(...comments.docs);
        }
        return allComments;
      }),
      // Get comments from articles subcollections
      getDocs(query(collection(db, 'articles'), where('userId', '==', userId))).then(async (articles) => {
        const allComments = [];
        for (const article of articles.docs) {
          const comments = await getDocs(collection(db, 'articles', article.id, 'comments'));
          allComments.push(...comments.docs);
        }
        return allComments;
      }),
      // Get comments from videos subcollections
      getDocs(query(collection(db, 'videos'), where('userId', '==', userId))).then(async (videos) => {
        const allComments = [];
        for (const video of videos.docs) {
          const comments = await getDocs(collection(db, 'videos', video.id, 'comments'));
          allComments.push(...comments.docs);
        }
        return allComments;
      }),
      // Get reactions from posts subcollections
      getDocs(query(collection(db, 'posts'), where('userId', '==', userId))).then(async (posts) => {
        const allReactions = [];
        for (const post of posts.docs) {
          const reactions = await getDocs(collection(db, 'posts', post.id, 'reactions'));
          allReactions.push(...reactions.docs);
        }
        return allReactions;
      }),
      // Get reactions from articles subcollections
      getDocs(query(collection(db, 'articles'), where('userId', '==', userId))).then(async (articles) => {
        const allReactions = [];
        for (const article of articles.docs) {
          const reactions = await getDocs(collection(db, 'articles', article.id, 'reactions'));
          allReactions.push(...reactions.docs);
        }
        return allReactions;
      }),
      // Get reactions from videos subcollections
      getDocs(query(collection(db, 'videos'), where('userId', '==', userId))).then(async (videos) => {
        const allReactions = [];
        for (const video of videos.docs) {
          const reactions = await getDocs(collection(db, 'videos', video.id, 'reactions'));
          allReactions.push(...reactions.docs);
        }
        return allReactions;
      })
    ]);

    // Delete all content
    userComments.docs.forEach(doc => batch.delete(doc.ref));
    userReactions.docs.forEach(doc => batch.delete(doc.ref));
    userPolls.docs.forEach(doc => batch.delete(doc.ref));
    userPosts.docs.forEach(doc => batch.delete(doc.ref));
    userVideos.docs.forEach(doc => batch.delete(doc.ref));
    userEvents.docs.forEach(doc => batch.delete(doc.ref));
    userRSVPs.docs.forEach(doc => batch.delete(doc.ref));
    userFriendRequests.docs.forEach(doc => batch.delete(doc.ref));
    userNotifications.docs.forEach(doc => batch.delete(doc.ref));
    userReports.docs.forEach(doc => batch.delete(doc.ref));
    userVotes.docs.forEach(doc => batch.delete(doc.ref));
    userEventComments.docs.forEach(doc => batch.delete(doc.ref));
    userPollVotes.docs.forEach(doc => batch.delete(doc.ref));
    userModeratorNotes.docs.forEach(doc => batch.delete(doc.ref));
    userArticles.docs.forEach(doc => batch.delete(doc.ref));
    
    // Delete all subcollection comments and reactions
    userPostComments.forEach(doc => batch.delete(doc.ref));
    userArticleComments.forEach(doc => batch.delete(doc.ref));
    userVideoComments.forEach(doc => batch.delete(doc.ref));
    userPostReactions.forEach(doc => batch.delete(doc.ref));
    userArticleReactions.forEach(doc => batch.delete(doc.ref));
    userVideoReactions.forEach(doc => batch.delete(doc.ref));

    // Note: All subcollection comments and reactions are already handled above

    // Remove user from all other users' friends lists
    const allUsers = await getDocs(collection(db, 'users'));
    for (const userDoc of allUsers.docs) {
      const userData = userDoc.data();
      if (userData.friends && userData.friends.includes(userId)) {
        batch.update(userDoc.ref, {
          friends: arrayRemove(userId),
          updatedAt: serverTimestamp()
        });
      }
    }

    // Delete the user document
    const userRef = doc(db, 'users', userId);
    batch.delete(userRef);

    // Delete the deletion request document (instead of just updating it)
    const requestRef = doc(db, 'dataDeletionRequests', requestId);
    batch.delete(requestRef);

    await batch.commit();
  } catch (error) {
    console.error('Error deleting user data:', error);
    throw error;
  }
};

export const cleanupExpiredBackups = async () => {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const backupsQuery = query(
      collection(db, 'userBackups'),
      where('createdAt', '<', Timestamp.fromDate(thirtyDaysAgo))
    );

    const backupsSnapshot = await getDocs(backupsQuery);
    const batch = writeBatch(db);

    backupsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  } catch (error) {
    console.error('Error cleaning up expired backups:', error);
    throw error;
  }
};

export const cleanupOldDeletionRequests = async () => {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Clean up old completed and rejected requests
    const completedQuery = query(
      collection(db, 'dataDeletionRequests'),
      where('status', 'in', ['completed', 'rejected']),
      where('createdAt', '<', Timestamp.fromDate(thirtyDaysAgo))
    );

    const completedSnapshot = await getDocs(completedQuery);
    const batch = writeBatch(db);

    completedSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    
    return completedSnapshot.size;
  } catch (error) {
    console.error('Error cleaning up old deletion requests:', error);
    throw error;
  }
};

export const cleanupEmptyModeratorNotes = async () => {
  try {
    // Find all moderator notes with empty or whitespace-only notes
    const emptyNotesQuery = query(
      collection(db, 'moderator_notes'),
      where('note', '==', '')
    );

    const emptyNotesSnapshot = await getDocs(emptyNotesQuery);
    const batch = writeBatch(db);

    emptyNotesSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Also find notes that are just whitespace
    const allNotesQuery = query(collection(db, 'moderator_notes'));
    const allNotesSnapshot = await getDocs(allNotesQuery);
    
    allNotesSnapshot.docs.forEach(doc => {
      const noteData = doc.data();
      if (noteData.note && noteData.note.trim().length === 0) {
        batch.delete(doc.ref);
      }
    });

    await batch.commit();
    
    return emptyNotesSnapshot.size;
  } catch (error) {
    console.error('Error cleaning up empty moderator notes:', error);
    throw error;
  }
};

export const getLastActionTimestamp = async (userId: string, action: string): Promise<Timestamp | null> => {
  try {
    const actionRef = doc(db, 'userActions', `${userId}_${action}`);
    const actionDoc = await getDoc(actionRef);

    if (actionDoc.exists()) {
      return actionDoc.data().timestamp;
    }

    return null;
  } catch (error) {
    console.error('Error getting last action timestamp:', error);
    return null;
  }
};

export const updateLastActionTimestamp = async (userId: string, action: string): Promise<void> => {
  try {
    const actionRef = doc(db, 'userActions', `${userId}_${action}`);
    await setDoc(actionRef, {
      timestamp: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating last action timestamp:', error);
    throw error;
  }
}; 